/*
 * File: GetStarted.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on April 20, 2025 at 11:54 PM
 * Copyright © 2025 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: May 22, 2025 at 12:20 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, { useEffect, forwardRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { Parser, ProcessNodeDefinitions } from 'html-to-react';

// Utilities
import { isValidNode, parseMarkdownLinks } from '../../../utilities/parser';

// Components
import { LocaleLink } from '../../LocaleLink';
import { Typography } from '../../Typography';
import { Button } from '../../Button';

// Constants
import { AVAILABLE_I18N_NAMESPACES } from '../../../../constants/base.constants';

// Styles
import * as S from './GetStarted.styles';


/**
 * Configuration
 */

// Parser
const ReactParser = new Parser();

// Node Definitions
const processNodeDefinitions = new ProcessNodeDefinitions();

// Parsing Instructions
const ParsingInstructions = [
	{
		shouldProcessNode(node) {
			return node.name === 'a';
		},
		processNode(node, children) {
			return <LocaleLink key={node.attribs.href} to={node.attribs.href} target="_blank">{children}</LocaleLink>;
		}
	},
	{
		shouldProcessNode() { return true; },
		processNode: processNodeDefinitions.processDefaultNode
	}
];


/**
 * Component
 */

export const GetStarted = forwardRef(({
	className, moveForward
}, ref) => {

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Get partner content
	const partnerContent = useMemo(() => partner?.content?.pages?.share_story?.components?.onboarding, [partner]);

	// Perform actions on component load
	useEffect(() => {}, []);

	// Return component
	return (
		<S.StepContainer ref={ref} className={className}>
			<S.InnerStep>

				{/* Content Container */}
				<S.ContentContainer>

					{/* Title */}
					<Typography tag="h1" weight="black">
						{!partnerContent?.title ? (
							<Trans t={t} i18nKey="pageShareStory.onboarding.title">
								Welcome to
								{' '}
								{{ partnerName: partner.name }}
								.
							</Trans>
						) : ReactParser.parseWithInstructions(parseMarkdownLinks(partnerContent?.title[locale.localeBaseId]), isValidNode, ParsingInstructions)}
					</Typography>

					{/* Purpose */}
					{!partnerContent?.purpose ? (
						<>
							<Typography tag="p">
								<Trans t={t} i18nKey="pageShareStory.onboarding.purpose">
									We want to hear your story. Don’t know where to start? Stories often have a beginning, middle, and end, although for many of us our healing is ongoing. Stories can focus on events, experiences, or emotions. For some, stories are used to depict the struggles and pain they have been through. For others, stories can depict the growth they feel or hope to feel. This is your story and you can tell it however you want. We are here to listen.
								</Trans>
							</Typography>
							<Typography tag="p">
								<Trans t={t} i18nKey="pageShareStory.onboarding.purpose2">
									Together we can create a wave of change.
								</Trans>
							</Typography>
						</>
					) : (
						<Typography tag="p">
							{ReactParser.parseWithInstructions(parseMarkdownLinks(partnerContent?.purpose[locale.localeBaseId]), isValidNode, ParsingInstructions)}
						</Typography>
					)}

					{/* Instructions */}
					{partnerContent?.instructions && partnerContent?.instructions.length > 0 && partnerContent?.instructions.map((instruction) => (
						<Typography tag="p" key={instruction[locale.localeBaseId]}>
							{ReactParser.parseWithInstructions(parseMarkdownLinks(instruction[locale.localeBaseId]), isValidNode, ParsingInstructions)}
						</Typography>
					))}

				</S.ContentContainer>

				{/* Continue Button */}
				<S.ActionContainer>
					<Button size="large" onClick={moveForward}>
						<Typography>
							<Trans t={t} i18nKey="actions.continue">
								Continue
							</Trans>
						</Typography>
						<FontAwesomeIcon icon={['fas', 'arrow-right-long']} />
					</Button>
				</S.ActionContainer>

				{/* Update Instructions */}
				<S.InstructionsContainer>
					<Typography tag="p" variation="3">
						<Trans t={t} i18nKey="pageShareStory.onboarding.updateInstructions">
							Looking to write an update to a story you&apos;ve already submitted?
							{' '}
							<LocaleLink to="/find-story">Click here to find your story</LocaleLink>
							.
						</Trans>
					</Typography>
				</S.InstructionsContainer>

				{/* Legal Snippet */}
				<S.LegalSnippet />

			</S.InnerStep>
		</S.StepContainer>
	);
});


/**
 * Configuration
 */

GetStarted.displayName = 'GetStarted';
GetStarted.propTypes = {
	className: PropTypes.string,
	moveForward: PropTypes.func
};
GetStarted.defaultProps = {
	className: null,
	moveForward: null
};
